import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';

import { Microfrontend } from '@cian/mf-react';
import type { ICianMFUIElement } from '@cian/mf-registry/browser';

import { IWidgetManifest } from 'shared/types/widgetManifest';
import { EAnalyticsEventType } from 'shared/types/analytics';
import { ERuntimeSettings } from 'shared/types/runtimeSettings';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { setAuthorizedModal } from 'shared/redux/landingSlice';

import { selectIsAuthorizedModalOpen } from '../../../selectors/landing/selectIsAuthorizedModalOpen';
import { selectRedirectInfoForAuth } from '../../../selectors/landing/selectRedirectInfoForAuth';
import { useTracking } from '../../../utils/tracking/useTracking';

export const LoginWidgetMicrofrontend = () => {
  const dispatch = useDispatch();
  const isAuthorizedModalOpen = useSelector(selectIsAuthorizedModalOpen);
  const { pathForRedirect, typeForRedirect, bankName, targetElementForRedirect } =
    useSelector(selectRedirectInfoForAuth);
  const {
    trackAuthorizationSuccess,
    trackRedirect,
    trackOpenPhoneAuthModal,
    trackOpenSmsAuthModal,
    trackClickOnRepeatSms,
    trackClickConfirmPhone,
    trackClickConfirmSms,
    trackAgreeToCommercial,
  } = useTracking();

  const { config, logger, telemetry } = useApplicationContext();
  const mfRef = useRef<ICianMFUIElement | null>(null);

  const handleCloseWidget = () => {
    dispatch(setAuthorizedModal({ isOpen: false }));
  };

  const handleUserAuth = () => {
    trackAuthorizationSuccess();
    const bankNameForRedirect = bankName || null;
    const type = typeForRedirect || null;
    const targetElement = targetElementForRedirect || null;

    telemetry.increment(EAnalyticsEventType.AuthSuccess);
    trackRedirect(type, targetElement, bankNameForRedirect);

    window.open(pathForRedirect, '_self');
  };

  const handleAuthWidgetAnalytics = (e: CustomEvent<EAnalyticsEventType>) => {
    switch (e.detail) {
      case EAnalyticsEventType.onPhoneScreenShown:
        telemetry.increment(EAnalyticsEventType.onPhoneScreenShown);
        trackOpenPhoneAuthModal();
        break;
      case EAnalyticsEventType.onPhoneScreenConfirm:
        trackClickConfirmPhone();
        break;
      case EAnalyticsEventType.onCodeScreenShown:
        telemetry.increment(EAnalyticsEventType.onCodeScreenShown);
        trackOpenSmsAuthModal();
        break;
      case EAnalyticsEventType.onCodeScreenConfirm:
        trackClickConfirmSms();
        break;
      case EAnalyticsEventType.onCodeScreenResend:
        trackClickOnRepeatSms();
        break;
      case EAnalyticsEventType.onAdvertisingConsent:
        trackAgreeToCommercial();
        break;
    }
  };

  useEffect(() => {
    // Сохраняем ссылку на ноду из ref.current, чтобы правильно убрать eventListener (иначе ругается линтер)
    const cleanupCurrent = mfRef.current;
    cleanupCurrent?.addEventListener('user-auth', handleUserAuth);
    cleanupCurrent?.addEventListener('close', handleCloseWidget);
    document.addEventListener('analytics', handleAuthWidgetAnalytics);

    return () => {
      cleanupCurrent?.removeEventListener('user-auth', handleUserAuth);
      cleanupCurrent?.removeEventListener('close', handleCloseWidget);
      document.removeEventListener('analytics', handleAuthWidgetAnalytics);
    };
  }, []);

  const manifest = config.get<IWidgetManifest>(ERuntimeSettings.loginWidgetManifest);

  if (!manifest) {
    logger.error('Отсутствует манифест виджета авторизации', {
      domain: 'microfrontends.login-widget-b2c.manifest',
    });

    return null;
  }

  return <Microfrontend ref={mfRef} {...manifest} parameters={{ isWidgetOpen: isAuthorizedModalOpen }} />;
};
